import { useLayoutEffect, useMemo, useRef } from 'react';

import { dialogService } from '@thefarmersfront/kitchen';

import { ModalSection, Table } from '~/components/common';

import { useClaimStore } from '~/store';
import {
  type TypeExchangeGoodsHandlers,
  type TypeExchangeGoodsRow,
  flattenExchangeGoods,
  getClaimExchangeRegisterExchangeItemsColumnDefs,
} from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type {
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
} from '~/types/claim.types';

import styles from '../ClaimExchangeRegisterSecondStepContent.module.scss';

type TypeExchangeOrderItems = TypeExchangeRegisterSecondStepValues['exchangeOrderItems'];
type TypeExchangeOrderItemsState = TypeExchangeRegisterSecondStepValuesState['exchangeOrderItems'];

const getRowId = (row: TypeExchangeGoodsRow) => row.rowId;

const ClaimExchangeRegisterSecondStepGoodsTable = ({
  data,
  dataState,
  onChange,
  onChangeState,
  onOpenExchangeItemAddModal,
}: {
  data: TypeExchangeOrderItems;
  dataState: TypeExchangeOrderItemsState;
  onChange: (values: Partial<TypeExchangeRegisterSecondStepValues>) => void;
  onChangeState: (values: Partial<TypeExchangeRegisterSecondStepValuesState>) => void;
  onOpenExchangeItemAddModal: (itemId: TypeExchangeOrderItems[number]['itemId']) => void;
}) => {
  const selectOptions = useClaimStore((state) => state.selectOptions.returnReason);

  const latestRef = useRef({ data, dataState, onChange, onChangeState, onOpenExchangeItemAddModal });
  useLayoutEffect(() => {
    latestRef.current = { data, dataState, onChange, onChangeState, onOpenExchangeItemAddModal };
  });

  const handlers = useMemo<TypeExchangeGoodsHandlers>(() => {
    const updateItems = (
      itemId: string,
      patch: (item: TypeExchangeOrderItems[number]) => TypeExchangeOrderItems[number]
    ) =>
      latestRef.current.onChange({
        exchangeOrderItems: latestRef.current.data.map((item) => (item.itemId === itemId ? patch(item) : item)),
      });
    const updateStates = (
      itemId: string,
      patch: (item: TypeExchangeOrderItemsState[number]) => TypeExchangeOrderItemsState[number]
    ) =>
      latestRef.current.onChangeState({
        exchangeOrderItems: latestRef.current.dataState.map((item) => (item.itemId === itemId ? patch(item) : item)),
      });
    const updateDeliveryItemQuantity = (itemId: string, deliveryItemId: string, quantity: string) =>
      updateItems(itemId, (item) => ({
        ...item,
        exchangeDeliveryItems: item.exchangeDeliveryItems.map((deliveryItem) =>
          deliveryItem.itemId === deliveryItemId ? { ...deliveryItem, quantity } : deliveryItem
        ),
      }));
    const resetDeliveryItemState = (itemId: string, deliveryItemId: string) =>
      updateStates(itemId, (item) => ({
        ...item,
        exchangeDeliveryItems: item.exchangeDeliveryItems.map((deliveryItem) =>
          deliveryItem.itemId === deliveryItemId ? { ...deliveryItem, quantity: DEFAULT_INPUT_STATUS } : deliveryItem
        ),
      }));

    return {
      onToggleExchange: (itemId, checked) => {
        updateItems(itemId, (item) => ({
          ...item,
          isExchange: checked,
          exchangeQuantity: '',
          exchangeReason: null,
          isSameGoods: false,
          exchangeDeliveryItems: [],
        }));
        updateStates(itemId, (item) => ({
          ...item,
          exchangeQuantity: DEFAULT_INPUT_STATUS,
          exchangeReason: DEFAULT_INPUT_STATUS,
          exchangeDeliveryItems: [],
        }));
      },
      onChangeQuantity: (itemId, value) => {
        updateItems(itemId, (item) => ({ ...item, exchangeQuantity: value }));
        updateStates(itemId, (item) => ({ ...item, exchangeQuantity: DEFAULT_INPUT_STATUS }));
      },
      onBlurQuantity: (itemId, value) => {
        const numberValue = Number(value.trim());
        const quantity = latestRef.current.data.find((item) => item.itemId === itemId)?.quantity ?? 0;

        if (numberValue === 0) {
          updateItems(itemId, (item) => ({ ...item, exchangeQuantity: '' }));
          return;
        }

        if (numberValue > quantity) {
          updateStates(itemId, (item) => ({ ...item, exchangeQuantity: { status: 'error' } }));

          dialogService.addDialog({
            children: '교환수량은 구매수량보다 같거나 적은 수량만 입력할 수 있습니다.',
            onConfirm: () => {
              updateItems(itemId, (item) => ({ ...item, exchangeQuantity: '' }));
              updateStates(itemId, (item) => ({ ...item, exchangeQuantity: DEFAULT_INPUT_STATUS }));
            },
          });
        }
      },
      onFocusQuantity: (itemId) =>
        updateStates(itemId, (item) => ({ ...item, exchangeQuantity: DEFAULT_INPUT_STATUS })),
      onChangeReason: (itemId, value) => {
        updateItems(itemId, (item) => ({ ...item, exchangeReason: value }));
        updateStates(itemId, (item) => ({ ...item, exchangeReason: DEFAULT_INPUT_STATUS }));
      },
      onToggleSameGoods: (itemId, checked) => {
        updateItems(itemId, (item) => ({
          ...item,
          isSameGoods: checked,
          exchangeDeliveryItems: checked ? [{ itemName: item.itemName, itemId: item.itemId, quantity: '' }] : [],
        }));
        updateStates(itemId, (item) => ({ ...item, exchangeDeliveryItems: [] }));
      },
      onOpenExchangeItemAddModal: (itemId) => latestRef.current.onOpenExchangeItemAddModal(itemId),
      onDeleteDeliveryItem: (itemId, deliveryItemId) => {
        updateItems(itemId, (item) => {
          const exchangeDeliveryItems = item.exchangeDeliveryItems.filter(
            (deliveryItem) => deliveryItem.itemId !== deliveryItemId
          );
          // 남은 교환배송 상품이 주문 상품 자신 하나뿐이면 동일상품 교환으로 본다.
          const isSameGoods = exchangeDeliveryItems.length === 1 && exchangeDeliveryItems[0].itemId === item.itemId;
          return { ...item, isSameGoods, exchangeDeliveryItems };
        });
        updateStates(itemId, (item) => ({
          ...item,
          exchangeDeliveryItems: item.exchangeDeliveryItems.filter(
            (deliveryItem) => deliveryItem.itemId !== deliveryItemId
          ),
        }));
      },
      onChangeDeliveryQuantity: (itemId, deliveryItemId, value) => {
        updateDeliveryItemQuantity(itemId, deliveryItemId, value);
        resetDeliveryItemState(itemId, deliveryItemId);
      },
      onBlurDeliveryQuantity: (itemId, deliveryItemId, value) => {
        if (Number(value.trim()) === 0) updateDeliveryItemQuantity(itemId, deliveryItemId, '');
      },
      onFocusDeliveryQuantity: (itemId, deliveryItemId) => resetDeliveryItemState(itemId, deliveryItemId),
    };
  }, []);

  const { columnDefs } = useMemo(
    () => getClaimExchangeRegisterExchangeItemsColumnDefs({ selectOptions, handlers }),
    [selectOptions, handlers]
  );

  const rows = useMemo(() => flattenExchangeGoods(data, dataState), [data, dataState]);

  return (
    <ModalSection className={styles['exchange-orders']} title="교환 상품">
      <div className={styles['table-wrapper']}>
        <Table data={rows} columns={columnDefs} getRowId={getRowId} keepScrollOnDataChange />
      </div>
    </ModalSection>
  );
};

export default ClaimExchangeRegisterSecondStepGoodsTable;
